'use client'

import { useCallback, useId, useMemo, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

function isValidEmail(email: string): boolean {
  // Pragmatic validation (server will validate again)
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

type SubscribeFormProps = {
  className?: string
  submitLabel?: string
  placeholder?: string
  source?: string
}

export function SubscribeForm({
  className,
  submitLabel = 'Subscribe',
  placeholder = 'Enter your email',
  source = 'unknown',
}: SubscribeFormProps) {
  const emailId = useId()
  const honeypotRef = useRef<HTMLInputElement | null>(null)

  const [email, setEmail] = useState('')
  const [consent, setConsent] = useState(false)
  const [loading, setLoading] = useState(false)
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [message, setMessage] = useState<string>('')

  const canSubmit = useMemo(() => {
    if (loading) return false
    if (status === 'success') return false
    return email.trim().length > 0
  }, [email, loading, status])

  const onSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      if (!canSubmit) return

      const normalizedEmail = email.trim().toLowerCase()
      if (!isValidEmail(normalizedEmail)) {
        setStatus('error')
        setMessage('Please enter a valid email address.')
        return
      }

      if (!consent) {
        setStatus('error')
        setMessage('Consent is required to subscribe.')
        return
      }

      // Honeypot bot protection
      const honeypot = honeypotRef.current?.value ?? ''
      if (honeypot.trim().length > 0) {
        setStatus('error')
        setMessage('Unable to subscribe. Please try again.')
        return
      }

      setLoading(true)
      setStatus('idle')
      setMessage('')

      try {
        const res = await fetch('/api/subscribe', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: normalizedEmail,
            source,
            consent: true,
            honeypot,
          }),
        })

        const data = (await res.json().catch(() => null)) as
          | { success?: boolean; message?: string; subscriberId?: string; code?: string; errors?: Record<string, string> }
          | null

        if (!res.ok) {
          const msg =
            (data && typeof data.message === 'string' && data.message) ||
            (data && data.code === 'DUPLICATE_EMAIL' ? "Email already subscribed" : '') ||
            'Failed to subscribe. Please try again.'
          setStatus('error')
          setMessage(msg)
          return
        }

        setStatus('success')
        setMessage((data && typeof data.message === 'string' && data.message) || 'Subscribed successfully!')
        setEmail('')
        setConsent(false)
      } catch {
        setStatus('error')
        setMessage('Failed to subscribe. Please try again.')
      } finally {
        setLoading(false)
      }
    },
    [canSubmit, consent, email, source]
  )

  const successMotion = {
    initial: { opacity: 0, y: 6, scale: 0.98 },
    animate: { opacity: 1, y: 0, scale: 1 },
    exit: { opacity: 0, y: 6, scale: 0.98 },
    transition: { duration: 0.22 },
  }

  return (
    <form onSubmit={onSubmit} className={className}>
      <div className="sr-only">
        <label htmlFor={emailId}>Email</label>
      </div>

      {/* Honeypot field */}
      <input
        ref={honeypotRef}
        type="text"
        name="company"
        tabIndex={-1}
        autoComplete="off"
        className="hidden"
        aria-hidden="true"
      />

      <div className="flex flex-col sm:flex-row gap-4">
        <input
          id={emailId}
          type="email"
          inputMode="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={placeholder}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          required
          disabled={loading || status === 'success'}
        />

        <button
          type="submit"
          disabled={!canSubmit}
          className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2 min-w-[120px]"
        >
          {loading ? 'Subscribing…' : submitLabel}
        </button>
      </div>

      <label className="mt-3 flex items-start gap-2 text-sm text-muted-foreground">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          disabled={loading || status === 'success'}
          className="mt-1"
          required
        />
        <span>I consent to receive newsletter emails. You can unsubscribe anytime.</span>
      </label>

      <AnimatePresence mode="wait">
        {message ? (
          <motion.p
            key={status + message}
            {...successMotion}
            className={`mt-3 text-sm font-medium ${status === 'error' ? 'text-destructive' : 'text-green-600 dark:text-green-400'}`}
            role={status === 'error' ? 'alert' : 'status'}
            aria-live="polite"
          >
            {message}
          </motion.p>
        ) : null}
      </AnimatePresence>
    </form>
  )
}
